/**
 * Instant trade (desktop): a floating, draggable one-click panel for the instrument on screen, its
 * and its settings (presets P1–P3 and the one-time risk notice).
 */
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import {
  clampLeverage,
  closePrice,
  D,
  fmt,
  fmtUsd,
  fmtUsdSigned,
  initialMargin,
  INSTANT_CLOSE_FRACTIONS,
  leverageCap,
  pairLabel,
  pnl,
  usdPerPoint,
  type InstantPreset,
  type InstantSizing,
  type InstantTradePrefs,
  type Side,
} from '@platform/shared';
import { ApiError } from '../lib/api';
import { useInstrument, useTicker } from '../stores/market';
import {
  activePreset,
  instantClose,
  instantFlip,
  instantMinFor,
  instantOpen,
  instantSizing,
  instantStore,
  saveInstantPrefs,
  setInstantOpen,
  setInstantPos,
  useInstant,
  useInstantPrefs,
} from '../stores/instant';
import { useAccountKind, useMe } from '../stores/session';
import { usePositions } from '../stores/trading';
import { NumInput } from './NumInput';
import { cx, Icon, Modal, Spinner } from './ui';
import { DEC_RE } from './useOrderForm';

const short = (amount: string) => {
  const d = D.from(amount);
  if (d.gte(1000) && d.div(1000).round(1).mul(1000).eq(d)) return `$${d.div(1000).toString()}K`;
  return `$${d.toString()}`;
};

/** A size that follows the price, as short as a preset amount: $835, $2.5K, $12K, $1.2M. */
const approx = (v: D) => {
  if (v.gte(1e6)) return `$${v.div(1e6).round(1).toString()}M`;
  if (v.gte(1e4)) return `$${v.div(1000).round(0).toString()}K`;
  if (v.gte(1e3)) return `$${v.div(1000).round(1).toString()}K`;
  if (v.gte(100)) return `$${v.round(0).toString()}`;
  return fmtUsd(v);
};

/** "$100", "$100 and $250", "$100, $250 and $500". */
const listOf = (xs: string[]) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

/** Toolbar button that opens the floating panel. */
export function InstantButton() {
  const { open } = useInstant();
  return (
    <button
      type="button"
      aria-pressed={open}
      onClick={() => setInstantOpen(!open)}
      className={cx('flex h-[28px] items-center gap-1.5 rounded-full border px-3 text-xs font-semibold', open ? 'border-accent bg-accent-bg text-accent-fg' : 'border-line-strong text-fg hover:border-accent')}
    >
      <Icon name="zap" size={13} />
      Instant trade
    </button>
  );
}

/** Net position, its PnL (incl. swap) and margin on one symbol, for the panel footer. */
function useSymbolPosition(symbol: string) {
  const positions = usePositions();
  const ticker = useTicker(symbol);
  const inst = useInstrument(symbol);
  return useMemo(() => {
    let qty = D.ZERO;
    let margin = D.ZERO;
    let u = D.ZERO;
    let n = 0;
    for (const p of positions) {
      if (p.symbol !== symbol) continue;
      n++;
      qty = p.side === 'long' ? qty.add(p.qty) : qty.sub(p.qty);
      margin = margin.add(p.marginHeld ?? p.margin);
      if (ticker && inst) u = u.add(pnl(p.side, p.entryPrice, closePrice(p.side, { bid: D.from(ticker.bid), ask: D.from(ticker.ask) }), p.qty, usdPerPoint(inst.contractSize, ticker.fx))).add(p.swap);
    }
    return { n, qty, margin, upnl: u };
  }, [positions, ticker, inst, symbol]);
}

interface RowProps {
  side: Side;
  symbol: string;
  preset: InstantPreset;
  /** What each amount opens at this price; null without one. */
  sizing: InstantSizing | null;
  leverage: number;
  maxOrder: string | null;
  disabled: boolean;
  busy: string | null;
}

function Row({ side, symbol, preset, sizing, leverage, maxOrder, disabled, busy }: RowProps) {
  const long = side === 'long';
  return (
    <div className="grid grid-cols-4 gap-1.5">
      {preset.amounts.map((a, i) => {
        const s = sizing?.sizes[i];
        const what = s
          ? `${long ? 'Long' : 'Short'} ${s.lots.toFixed(sizing!.lotDp)} lots ${pairLabel(symbol)} ≈ ${fmtUsd(s.value)} · margin ${fmtUsd(initialMargin(s.value, leverage))}`
          : `${long ? 'Long' : 'Short'} ${fmtUsd(a)}`;
        const tip = s?.overMax && maxOrder ? `Above ${pairLabel(symbol)}'s max order of ${fmtUsd(maxOrder)}` : what;
        return (
          <button
            key={i}
            type="button"
            disabled={disabled || !!s?.overMax}
            onClick={() => void instantOpen(symbol, side, i)}
            title={tip}
            aria-label={tip}
            className={cx(
              'num h-8 rounded-full border text-[12px] font-semibold disabled:opacity-40',
              long ? 'border-up text-up hover:bg-up-bg' : 'border-down text-down hover:bg-down-bg',
            )}
          >
            {/* A row started at the minimum names what each button is worth now; otherwise the client's own amount. */}
            {busy === `${side}:${i}` ? <Spinner /> : s && sizing!.fitted ? approx(s.value) : short(a)}
          </button>
        );
      })}
    </div>
  );
}

/** Floating one-click panel for `symbol`. Drag it by the top bar; it remembers where it was left. */
export function InstantPanel({ symbol }: { symbol: string }) {
  const { open, pos, notice, busy, prefs } = useInstant();
  const me = useMe();
  const kind = useAccountKind();
  const ticker = useTicker(symbol);
  const inst = useInstrument(symbol);
  const position = useSymbolPosition(symbol);
  const [settings, setSettings] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);

  // Keep the panel on screen when the window shrinks.
  const [vw, setVw] = useState(() => window.innerWidth);
  useEffect(() => {
    const on = () => setVw(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  if (!open) return null;
  const p = activePreset(prefs);
  // The instrument's range, with the client's own cap (set by staff) when lower than its maximum.
  const lev = inst ? clampLeverage(p.leverage, { minLeverage: inst.minLeverage, maxLeverage: leverageCap(inst.maxLeverage, me?.maxLeverage) }) : p.leverage;
  const longSizing = inst && ticker ? instantSizing(inst, ticker, 'long', p.amounts) : null;
  const shortSizing = inst && ticker ? instantSizing(inst, ticker, 'short', p.amounts) : null;
  // Why the row starts at the minimum, named once for the panel (from the Long side unless only Short is fitted).
  const fit = longSizing?.fitted ? longSizing : shortSizing?.fitted ? shortSizing : null;
  const pair = pairLabel(symbol);
  const fitNote = fit
    ? (() => {
        const under = p.amounts.filter((_, i) => fit.below[i]).map(short);
        const sizes = under.length === p.amounts.length ? 'sizes' : listOf(under);
        return `P${prefs.active + 1}'s ${sizes} ${under.length === 1 ? 'buys' : 'buy'} less than ${pair}'s minimum of ${fit.min.lots.toFixed(fit.lotDp)} lots (≈ ${approx(fit.min.value)}), so sizes start there.`;
      })()
    : null;
  const paused = !!ticker?.paused && kind === 'live';
  const blocked = !me ? 'Log in to trade' : !ticker ? 'No price yet' : !ticker.open ? 'Market is closed' : paused ? 'Live price unavailable, trading is paused' : null;
  const W = 300;
  const x = Math.max(8, Math.min(pos?.x ?? vw - W - 350, vw - W - 8));
  // Default: top-right of the chart, clear of the instrument header.
  const y = Math.max(8, Math.min(pos?.y ?? 250, window.innerHeight - 120));

  const onDown = (e: ReactPointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    const r = ref.current!.getBoundingClientRect();
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = (e: ReactPointerEvent) => {
    if (!drag.current) return;
    setInstantPos({ x: e.clientX - drag.current.dx, y: e.clientY - drag.current.dy });
  };
  const onUp = () => {
    drag.current = null;
  };
  const setActive = (i: 0 | 1 | 2) => {
    const next = { ...prefs, active: i };
    instantStore.set({ prefs: next });
    if (me) void saveInstantPrefs(next).catch(() => {});
  };

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={`Instant trade ${pairLabel(symbol)}`}
      style={{ left: x, top: y, width: W }}
      className="fixed z-40 rounded-xl border border-line-strong bg-panel text-[12px] shadow-[var(--float-shadow)]"
    >
      <div onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} className="flex cursor-grab touch-none items-center gap-1 border-b border-line px-2.5 py-2 select-none active:cursor-grabbing">
        <Icon name="dots" size={14} className="text-muted" />
        {(['P1', 'P2', 'P3'] as const).map((l, i) => (
          <button
            key={l}
            type="button"
            aria-pressed={prefs.active === i}
            onClick={() => setActive(i as 0 | 1 | 2)}
            className={cx('num rounded px-1.5 py-0.5 text-[11px] font-semibold', prefs.active === i ? 'bg-accent-bg text-accent-fg' : 'text-muted hover:text-fg')}
          >
            {l}
          </button>
        ))}
        <button type="button" onClick={() => setSettings(true)} aria-label="Instant trade settings" className="ml-1 grid size-6 place-items-center rounded text-muted hover:text-fg">
          <Icon name="edit" size={13} />
        </button>
        <span className="num ml-auto truncate text-[11px] text-muted">{pairLabel(symbol)}</span>
        <button type="button" onClick={() => setInstantOpen(false)} aria-label="Close instant trade" className="grid size-6 place-items-center rounded text-muted hover:text-fg">
          <Icon name="close" size={13} />
        </button>
      </div>

      {!prefs.enabled ? (
        <div className="flex flex-col gap-2.5 p-3">
          <p className="m-0 text-muted">Instant trade places market orders with one click, with no confirmation step.</p>
          <button type="button" onClick={() => setSettings(true)} className="h-8 rounded-lg bg-accent font-semibold text-white">
            Set up instant trade
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2 border-b border-line p-2.5">
            <div className="flex items-center gap-1.5 font-semibold">
              <span>Long</span>
              {fit && <span className="rounded-full bg-accent-bg px-1.5 py-px text-[10.5px] font-semibold text-accent-fg">Sized for {pair.split('/')[0]}</span>}
              <span className="num ml-auto font-normal text-muted">
                {lev}x{p.tpPct ? ` · TP ${p.tpPct}%` : ''}
                {p.slPct ? ` · SL ${p.slPct}%` : ''}
              </span>
            </div>
            <Row side="long" symbol={symbol} preset={p} sizing={longSizing} leverage={lev} maxOrder={inst?.maxNotional ?? null} disabled={!!blocked || !!busy} busy={busy} />
          </div>
          <div className="flex flex-col gap-2 border-b border-line p-2.5">
            <div className="font-semibold">Short</div>
            <Row side="short" symbol={symbol} preset={p} sizing={shortSizing} leverage={lev} maxOrder={inst?.maxNotional ?? null} disabled={!!blocked || !!busy} busy={busy} />
          </div>
          <div className="flex flex-col gap-2 border-b border-line p-2.5">
            <div className="flex justify-between font-semibold">
              <span>Close</span>
              <span className={cx('num font-normal', position.qty.isPos() ? 'text-up' : position.qty.isNeg() ? 'text-down' : 'text-muted')}>
                {position.n ? `Net ${position.qty.isPos() ? 'long' : position.qty.isNeg() ? 'short' : 'flat'} ${fmt(position.qty.abs(), inst?.qtyDecimals ?? 4)}` : 'No position'}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {INSTANT_CLOSE_FRACTIONS.map((f) => (
                <button key={f} type="button" disabled={!position.n || !!busy || !me} onClick={() => void instantClose(symbol, f)} className="num h-8 rounded-full border border-line-strong text-[12px] font-semibold text-fg hover:bg-raised disabled:opacity-40">
                  {busy === `close:${f}` ? <Spinner /> : `${D.from(f).mul(100).toFixed(0)}%`}
                </button>
              ))}
              <button type="button" disabled={!position.n || position.qty.isZero() || !!busy || !!blocked} onClick={() => void instantFlip(symbol)} className="h-8 rounded-full border border-line-strong text-[12px] font-semibold text-fg hover:bg-raised disabled:opacity-40">
                {busy === 'flip' ? <Spinner /> : 'Flip'}
              </button>
            </div>
          </div>
          <div className="num grid grid-cols-2 gap-x-3 gap-y-0.5 px-2.5 py-2 text-[11px]">
            <span className="text-muted">Margin</span>
            <span className="text-right">{fmtUsd(position.margin)}</span>
            <span className="text-muted">PnL</span>
            <span className={cx('text-right', position.upnl.isPos() ? 'text-up' : position.upnl.isNeg() ? 'text-down' : '')}>{position.n ? fmtUsdSigned(position.upnl) : '—'}</span>
          </div>
        </>
      )}
      {fitNote && prefs.enabled && !blocked && (
        <div className="flex items-start gap-1.5 border-t border-line px-2.5 py-2 text-[11.5px] leading-snug text-muted">
          <Icon name="info" size={13} className="mt-px shrink-0 text-accent-fg" />
          <span>
            {fitNote}{' '}
            <button type="button" onClick={() => setSettings(true)} className="font-semibold text-accent-fg hover:underline">
              Edit sizes
            </button>
          </span>
        </div>
      )}
      {(blocked || notice) && prefs.enabled && (
        <p role="status" className={cx('m-0 border-t border-line px-2.5 py-2 text-[11.5px]', blocked ? 'text-warn' : notice!.ok ? 'text-up' : 'text-down')}>
          {blocked ?? notice!.text}
        </p>
      )}
      <InstantSettings open={settings} onClose={() => setSettings(false)} symbol={symbol} />
    </div>
  );
}

// ---------- settings ----------

const field = 'num h-full w-full min-w-0 bg-transparent text-[12.5px] outline-none placeholder:text-muted/70';
const sectionLabel = 'flex items-baseline justify-between text-[11px] font-semibold tracking-wide text-muted uppercase';

/** Input box with a unit before and/or after the value; `warn` outlines it in amber. */
function Affixed({ pre, post, warn, children }: { pre?: string; post?: string; warn?: boolean; children: ReactNode }) {
  return (
    <div className={cx('num flex h-[34px] min-w-0 items-center gap-1 rounded-md border bg-bg px-2.5 text-[12px] text-muted focus-within:border-accent', warn ? 'border-warn/60' : 'border-line')}>
      {pre && <span aria-hidden="true">{pre}</span>}
      {children}
      {post && <span aria-hidden="true">{post}</span>}
    </div>
  );
}

function Switch({ on }: { on: boolean }) {
  return (
    <span aria-hidden="true" className={cx('relative h-5 w-9 shrink-0 rounded-full transition-colors', on ? 'bg-accent' : 'bg-line-strong')}>
      <span className={cx('absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow transition-transform', on && 'translate-x-4')} />
    </span>
  );
}

/** `symbol`: the instrument on screen, whose smallest order the sizes are checked against. */
export function InstantSettings({ open, onClose, symbol }: { open: boolean; onClose: () => void; symbol?: string }) {
  const prefs = useInstantPrefs();
  const me = useMe();
  const inst = useInstrument(symbol ?? '');
  const ticker = useTicker(symbol ?? '');
  const [draft, setDraft] = useState<InstantTradePrefs>(prefs);
  const [tab, setTab] = useState<0 | 1 | 2>(prefs.active);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setDraft(prefs);
      setTab(prefs.active);
      setErr(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const p = draft.presets[tab];
  const setP = (patch: Partial<InstantPreset>) =>
    setDraft((d) => ({ ...d, presets: d.presets.map((x, i) => (i === tab ? { ...x, ...patch } : x)) as InstantTradePrefs['presets'] }));
  const num = (v: string) => DEC_RE.test(v);
  const min = inst && ticker ? instantMinFor(inst, ticker, 'long') : null;
  // Each amount on its own: does it buy less than the instrument's smallest order?
  const below = p.amounts.map((a) => !!(inst && ticker && D.isValid(a) && D.from(a).isPos() && instantSizing(inst, ticker, 'long', [a])?.below[0]));

  const save = async () => {
    if (!me) {
      setErr('Log in to save presets');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await saveInstantPrefs(draft);
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Instant trade settings" width={440}>
      <div role="tablist" aria-label="Preset" className="grid grid-cols-3 rounded-lg bg-raised p-[3px]">
        {(['P1', 'P2', 'P3'] as const).map((l, i) => (
          <button
            key={l}
            type="button"
            role="tab"
            aria-selected={tab === i}
            onClick={() => setTab(i as 0 | 1 | 2)}
            className={cx('flex h-[30px] items-center justify-center gap-1.5 rounded-md text-[12.5px] font-semibold', tab === i ? 'bg-bg text-fg shadow-sm' : 'text-muted')}
          >
            {draft.active === i && <span aria-label="active" className="size-1.5 rounded-full bg-accent" />}
            {l}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        <div className={sectionLabel}>
          One-click sizes <span className="font-normal tracking-normal normal-case">USDT position size</span>
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {p.amounts.map((a, i) => (
            <Affixed key={i} pre="$" warn={below[i]}>
              <NumInput
                value={a}
                aria-label={`Size ${i + 1}`}
                onValue={(v) => num(v) && setP({ amounts: p.amounts.map((x, j) => (j === i ? v : x)) as InstantPreset['amounts'] })}
                className={cx(field, below[i] ? 'text-warn' : 'text-fg')}
              />
            </Affixed>
          ))}
        </div>
        {symbol && min && (
          <p className="m-0 flex items-start gap-1.5 text-[11.5px] leading-snug text-muted">
            <Icon name="info" size={13} className={cx('mt-px shrink-0', below.some(Boolean) ? 'text-warn' : 'text-accent-fg')} />
            <span>
              On {pairLabel(symbol)} the smallest order is {min.lots.toFixed(min.lotDp)} lots (≈ {approx(min.value)}).
              {below.some(Boolean) && ' Sizes in amber are below it, so there the row starts at the minimum and keeps these proportions.'}
            </span>
          </p>
        )}
      </div>
      <label className="flex flex-col gap-1 text-[12px] text-muted">
        Leverage
        <Affixed post="×">
          <NumInput value={String(p.leverage)} onValue={(v) => /^\d{0,4}$/.test(v) && setP({ leverage: Number(v) || 0 })} className={cx(field, 'text-fg')} />
        </Affixed>
      </label>

      <div className="flex flex-col gap-2">
        <div className={sectionLabel}>
          Take profit / Stop loss <span className="font-normal tracking-normal normal-case">optional</span>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <Affixed pre="TP" post="%">
            <NumInput value={p.tpPct ?? ''} placeholder="None" aria-label="Take profit %" onValue={(v) => num(v) && setP({ tpPct: v || null })} className={cx(field, 'text-up')} />
          </Affixed>
          <Affixed pre="SL" post="%">
            <NumInput value={p.slPct ?? ''} placeholder="None" aria-label="Stop loss %" onValue={(v) => num(v) && setP({ slPct: v || null })} className={cx(field, 'text-down')} />
          </Affixed>
        </div>
        <p className="m-0 text-[11.5px] text-muted">Distance from your entry price. Leverage is capped per instrument.</p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-[13px] font-medium">Active preset</span>
          <span className="text-[12px] text-muted">The active preset drives the panel.</span>
        </div>
        {draft.active === tab ? (
          <span className="shrink-0 rounded-md bg-accent-bg px-2.5 py-1.5 text-[12px] font-semibold text-accent-fg">P{tab + 1} active ✓</span>
        ) : (
          <button type="button" onClick={() => setDraft({ ...draft, active: tab })} className="h-8 shrink-0 rounded-md border border-line-strong px-3 text-[12px] font-semibold text-fg hover:bg-raised">
            Set P{tab + 1} as active
          </button>
        )}
      </div>

      <div className="-mx-5 h-px bg-line" />

      <button
        type="button"
        role="switch"
        aria-checked={draft.enabled}
        onClick={() => setDraft({ ...draft, enabled: !draft.enabled })}
        className="flex items-center gap-3 rounded-lg border border-warn/30 bg-warn-bg p-3 text-left"
      >
        <span className="flex flex-col gap-1">
          <span className="flex items-center gap-1.5 text-[13px] font-semibold text-fg">
            <span aria-hidden="true" className="text-warn">⚠</span>
            Turn on one-click trading
          </span>
          <span className="text-[12px] text-muted">The panel places market orders instantly, with no confirmation. Leveraged trading can lose your whole margin quickly.</span>
        </span>
        <Switch on={draft.enabled} />
      </button>

      {err && (
        <p className="m-0 text-xs text-down" role="alert">
          {err}
        </p>
      )}
      <div className="-mx-5 -mb-5 grid grid-cols-2 gap-2.5 rounded-b-xl border-t border-line bg-bg px-5 py-4">
        <button type="button" onClick={onClose} className="h-[40px] rounded-lg border border-line-strong bg-panel font-semibold text-fg">
          Cancel
        </button>
        <button type="button" onClick={() => void save()} disabled={saving} className="h-[40px] rounded-lg bg-accent font-semibold text-white disabled:opacity-60">
          {saving ? <Spinner /> : 'Save'}
        </button>
      </div>
    </Modal>
  );
}
